/**
 * CaribbeanLeaf API Client
 *
 * MIRRORS: caribbeanleaf-api/src/utils/api_response.py
 *
 * Fetch-based HTTP client with:
 * - Bearer token authentication
 * - Request timeout handling
 * - Consistent error normalization
 * - TypeScript type safety
 */

import { API_URL, DEFAULT_TIMEOUT } from '@/config/api';
import type {
  ApiSuccessResponse,
  ApiError,
  PaginatedResponse,
} from '@/types/api';

// Storage keys
const TOKEN_KEY = 'caribbeanleaf_token';
const REFRESH_TOKEN_KEY = 'caribbeanleaf_refresh_token';
const TENANT_KEY = 'caribbeanleaf_tenant_slug';

// Refresh lock — prevents multiple simultaneous refresh attempts
let refreshPromise: Promise<boolean> | null = null;

/**
 * Get stored access token
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Store tokens after login
 */
export function setTokens(accessToken: string, refreshToken?: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

/**
 * Store tenant slug for X-Tenant-ID header
 */
export function setTenantSlug(slug: string): void {
  localStorage.setItem(TENANT_KEY, slug);
}

/**
 * Get stored tenant slug, falling back to build-time default
 */
export function getTenantSlug(): string | null {
  return localStorage.getItem(TENANT_KEY) || import.meta.env.VITE_DEFAULT_TENANT || null;
}

/**
 * Get stored refresh token
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Clear tokens on logout
 */
export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TENANT_KEY);
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Uses a promise lock so only one refresh happens at a time.
 * Returns true if refresh succeeded, false otherwise.
 */
async function tryRefreshToken(): Promise<boolean> {
  // Reuse in-flight refresh if one is already happening
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;

    try {
      const url = `${API_URL}/auth/refresh`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) return false;

      const data = await response.json();
      if (data.success && data.data) {
        setTokens(data.data.access_token, data.data.refresh_token);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/**
 * Logout with server-side token revocation.
 * Calls the revocation endpoint before clearing local tokens.
 * Defensive: clears tokens even if the API call fails.
 */
export async function serverLogout(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      const url = `${API_URL}/auth/logout`;
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      // Network error — still clear tokens below
    }
  }
  clearTokens();
  window.dispatchEvent(new CustomEvent('auth:logout'));
}

/**
 * Normalized API error structure
 */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * Request options extending fetch RequestInit
 */
interface RequestOptions extends Omit<RequestInit, 'body'> {
  timeout?: number;
  params?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
}

/**
 * Build URL with query parameters
 */
function buildUrl(
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  const url = new URL(`${API_URL}${path}`, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}

/**
 * Execute fetch with timeout
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeout: number
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Parse response and handle errors
 */
async function parseResponse<T>(response: Response): Promise<T> {
  // Parse JSON response
  let data: ApiSuccessResponse<T> | ApiError;
  try {
    data = await response.json();
  } catch {
    throw new ApiClientError(
      'Invalid JSON response',
      response.status,
      'PARSE_ERROR'
    );
  }

  // Check for API-level success
  if (!response.ok || !('success' in data) || !data.success) {
    const error = data as ApiError;
    throw new ApiClientError(
      error.message || `Request failed with status ${response.status}`,
      response.status,
      error.code || 'API_ERROR',
      error.details
    );
  }

  return (data as ApiSuccessResponse<T>).data;
}

/**
 * Core request function
 */
async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const {
    timeout = DEFAULT_TIMEOUT,
    params,
    body,
    headers,
    ...fetchOptions
  } = options;

  // Build headers
  const requestHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // Add auth token if available
  const token = getToken();
  if (token) {
    (requestHeaders as Record<string, string>)['Authorization'] =
      `Bearer ${token}`;
  }

  // Add tenant context header
  const tenantSlug = getTenantSlug();
  if (tenantSlug) {
    (requestHeaders as Record<string, string>)['X-Tenant-ID'] = tenantSlug;
  }

  // Build request
  const url = buildUrl(path, params);
  const init: RequestInit = {
    method,
    headers: requestHeaders,
    ...fetchOptions,
  };

  // Add body for non-GET requests
  if (body && method !== 'GET') {
    init.body = JSON.stringify(body);
  }

  try {
    let response = await fetchWithTimeout(url, init, timeout);

    // Handle 401 — try refresh before giving up
    if (response.status === 401) {
      // Don't retry if this was itself the refresh endpoint
      const isRefreshCall = path.includes('/auth/refresh');
      if (!isRefreshCall) {
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          // Retry with new token
          const newToken = getToken();
          if (newToken) {
            (init.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
          }
          response = await fetchWithTimeout(url, init, timeout);
          if (response.status !== 401) {
            return parseResponse<T>(response);
          }
        }
      }

      // Refresh failed or wasn't attempted — logout
      await serverLogout();
      throw new ApiClientError('Session expired', 401, 'UNAUTHORIZED');
    }

    return parseResponse<T>(response);
  } catch (error) {
    // Handle timeout
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiClientError('Request timeout', 0, 'TIMEOUT');
    }

    // Handle network errors
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new ApiClientError(
        'Network error - please check your connection',
        0,
        'NETWORK_ERROR'
      );
    }

    // Re-throw ApiClientError
    if (error instanceof ApiClientError) {
      throw error;
    }

    // Wrap unknown errors
    throw new ApiClientError(
      error instanceof Error ? error.message : 'Unknown error',
      0,
      'UNKNOWN_ERROR'
    );
  }
}

/**
 * API Client with typed methods
 */
export const apiClient = {
  /**
   * GET request
   */
  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('GET', path, options);
  },

  /**
   * POST request
   */
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('POST', path, { ...options, body });
  },

  /**
   * PUT request
   */
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PUT', path, { ...options, body });
  },

  /**
   * PATCH request
   */
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PATCH', path, { ...options, body });
  },

  /**
   * DELETE request
   */
  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('DELETE', path, options);
  },

  /**
   * GET with pagination support.
   *
   * The backend returns items in `data` (array) and pagination in
   * `meta.pagination`. This method assembles both into a single
   * PaginatedResponse<T> so callers get `{ data, pagination }`.
   */
  async getPaginated<T>(
    path: string,
    options?: RequestOptions
  ): Promise<PaginatedResponse<T>> {
    const {
      timeout = DEFAULT_TIMEOUT,
      params,
      headers,
      body: _body,
      ...fetchOptions
    } = options || {};

    const requestHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      ...headers,
    };

    const token = getToken();
    if (token) {
      (requestHeaders as Record<string, string>)['Authorization'] =
        `Bearer ${token}`;
    }

    const tenantSlug = getTenantSlug();
    if (tenantSlug) {
      (requestHeaders as Record<string, string>)['X-Tenant-ID'] = tenantSlug;
    }

    const url = buildUrl(path, params);

    const response = await fetchWithTimeout(
      url,
      { method: 'GET', headers: requestHeaders, ...fetchOptions },
      timeout
    );

    if (response.status === 401) {
      await serverLogout();
      throw new ApiClientError('Session expired', 401, 'UNAUTHORIZED');
    }

    let json: Record<string, unknown>;
    try {
      json = await response.json();
    } catch {
      throw new ApiClientError('Invalid JSON response', response.status, 'PARSE_ERROR');
    }

    if (!response.ok || !json.success) {
      const msg = (json.message || json.error || 'Request failed') as string;
      throw new ApiClientError(msg, response.status, (json.code as string) || 'API_ERROR');
    }

    const meta = json.meta as { pagination?: Record<string, number> } | undefined;
    const pagination = meta?.pagination;

    return {
      data: json.data as T[],
      pagination: {
        page: pagination?.page ?? 1,
        page_size: pagination?.page_size ?? 20,
        total: pagination?.total_items ?? (json.data as T[]).length,
        total_pages: pagination?.total_pages ?? 1,
      },
    };
  },
};

/**
 * Check if error is an API client error
 */
export function isApiClientError(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError;
}

/**
 * Extract user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  if (isApiClientError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

export default apiClient;
